import { AxiosResponse } from 'axios';
import { HttpClient } from './httpClient';
import { KitPrePaid, PlatformSetting, UpdatePlatformSettingRequest, CreateOrderRequest, DuplicateOrderResponse, OrderItemAttachment, OrderItem, OrderItemReports, OrdersResponse, OrganizationsResponse, SignInRequest, SignInResponse, SwitchOrgRequest, SwitchOrgResponse, UpdateOrderRequest, UpdateOrderResponse, UsersResponse, OrderSearchParams, UserProfile, CreateOrganizationRequest, OrganizationItem, UpdateOrganizationRequest, OrganizationsSearchResponse, CreateUserRequest, OrganizationSearchParams, UsersSearchParams, UsersSearchResponse, PasswordResetChangeRequest, UserRole, UserGroupsResponse, CreateUserGroupRequest, UserGroup, UpdateUserGroupRequest, UserGroupTest, CreateOrganizationBillingRequest, OrganizationBilling, OrganizationBillingReport, OrganizationActivityReport, CreateTransactionRequest, CreateTransactionResponse, UserGroupTestPrice, TermsConditionsAcceptance, AccountRep, AccountRepsResponse, OrderStats, KitItem, KitSearchParams, CreateKitOrderRequest, KitOrderConfirmation, SkuCode, SkuSyncResult, CreateShipToPatientOrderRequest, ShipToPatientOrderConfirmation, PaymentLinkInfo, CompletePaymentLinkRequest, CompletePaymentLinkConfirmation, SendVerificationCodeResponse, VerifyCodeRequest, ChargeSubmissionFeeRequest, ChargeSubmissionFeeConfirmation, CheckTrackingResult, AdHocTrackingResult, OrderTrackingResult, CheckAllTrackingResult, TrackingRunStarted, SchedulerRunStatus, SchedulerRunHistoryItem, CreateUserGroupTestRequest, UpdateUserGroupTestRequest, UpsertUserGroupTestPriceRequest, TestCatalogEntry, CreateTestCatalogEntryRequest, UpdateTestCatalogEntryRequest, PlatformFee, UpdatePlatformFeeRequest, Enterprise, CreateEnterpriseRequest, UpdateEnterpriseRequest, HealthCheckResponse, HealthVersion, CheckPendingVerificationResult, NuviaOrderSearchParams, NuviaOrdersResponse, NuviaOrderItem, SpecimenSource, CreateSpecimenSourceRequest, SetTestSpecimenSourcesRequest, SystemBannerItem, CreateSystemBannerRequest, UpdateSystemBannerRequest, SystemBannerStats, AnalyticsDashboard, StripeSetupOrgs, RevenueLabAnalytics, RevenueLabPeriod, RevenueLabPeriodParams, RevenueDetailBucket, RevenueDetailRow, LabDetailBucket, LabDetailRow, TermsAcceptanceStatus, OrdersDetail, ActiveUserDetailRow, ActiveOrgDetailRow, LoginUserDetailRow, ErrorDetailRow, BannerViewsDetailRow, SuspendedOrgDetailRow, NeedsContactDetailRow, UnparseableDateDetailRow, TurnaroundDetailRow, NuviaOrderDetailRow, PreBuyOrgDetailRow, SchedulerRunDetailRow, ShippingRequestDetailRow, PendingPaymentDetailRow, BillingHistoryDetailRow, ChargeAuditDetailRow, BillingSourceOrgDetailRow, DelayedShipmentDetailRow, TransitTimeDetailRow, UnknownCheckDetailRow, ReshipDetailRow, OrdersAwaitingNextCheckDetailRow, ReturnTrackingCaptureDetailRow, PatientDetailRow, NewClientDetailRow, ClientSampleDetailRow, ClientResultedDetailRow, NotificationDelivery, NotificationDeliveryFilter, ExecutiveReport, ExecutiveReportRevenueEventDetail, ExecutiveReportCashRowDetail, ExecutiveReportSampleDetail, ExecutiveReportBacklogDetail, ExecutiveReportKitFloatDetail, ExecutiveReportTestCreditDetail, RefundedChargeDetailRow } from './types';
import { KitLookupLocal, KitLookupNuvia, KitLookupUps, KitLookupSubLot, KitLookupFlag, CreateKitLookupFlagRequest, ReviewKitLookupFlagRequest } from './types';
import { CommercialReport, CommercialReportFilters, CommercialReportRequest } from './types';
import { PurchaseHistoryResponse, PurchaseReceipt, ReverseChargeRequest, ReverseChargeResult, ChargeReconciliationRunHistoryItem, OrphanedCharge } from './types';
import { OrganizationStripeBilling, ResolvedPaymentSource, StripePaymentIntentRequest, OrderTrackingMaintenanceItem, OrderTrackingMaintenanceFilter, UpdateOrderTrackingResult, RefreshRequest, RefreshResponse, CardFundingBackfillStarted, CardFundingBackfillStatus, CardFundingBackfillHistoryItem } from './types';
import { AccountProfile } from '../account/types';
import { UserItem } from './types';
import { OrganizationGridParams } from '../organizations/search/types';

export class SimplyTestApi {
    private readonly baseUrl: string;
    private readonly http: HttpClient;
  
    constructor(baseUrl: string, idToken?: string) {
      this.baseUrl = baseUrl;
      this.http = new HttpClient({
        baseURL: this.baseUrl,
        withCredentials: false,
        headers: {
          ...(idToken ? { 'Authorization' : `Bearer ${idToken}` } : {}),
      },
      validateStatus: (_) => true,
      });
    }

    public async signIn(req: SignInRequest): Promise<AxiosResponse<SignInResponse>> {
      return await this.http.post<SignInRequest, SignInResponse>(`/auth/signin`, req);
    }

    // Org Switcher feature - reissues a token scoped to the picked org (User Group Manager only)
    public async switchOrg(req: SwitchOrgRequest): Promise<AxiosResponse<SwitchOrgResponse>> {
      return await this.http.post<SwitchOrgRequest, SwitchOrgResponse>(`/auth/switch-org`, req);
    }

    // Sep 2026 - refresh-token flow - exposed here for completeness/direct use, though
    // httpClient.ts's own silent-refresh interceptor calls this endpoint directly (a bare axios
    // call, not through this class) so it isn't itself subject to the interceptor it's part of.
    public async refresh(req: RefreshRequest): Promise<AxiosResponse<RefreshResponse>> {
      return await this.http.post<RefreshRequest, RefreshResponse>(`/auth/refresh`, req);
    }

    // Sep 2026 - "we need to fix this properly" - signing out was previously entirely client-side
    // (clearing Redux/localStorage only), so a refresh token stayed valid server-side for its full
    // 14-day lifetime even after an explicit sign-out. Called from auth/redux/sagas.ts's
    // `unauthorized` handler - the one place auth actually gets cleared, whatever triggered it.
    public async revoke(req: RefreshRequest): Promise<AxiosResponse<void>> {
      return await this.http.post<RefreshRequest, void>(`/auth/revoke`, req);
    }

    public async resetPassword(email?: string): Promise<AxiosResponse<void>> {
      return await this.http.post(`/auth/passwordreset/${encodeURIComponent(email!)}/request`);
    }

    public async resetPasswordConfirm(req: PasswordResetChangeRequest, userId?: string, token?: string): Promise<AxiosResponse<void>> {
      return await this.http.post(`/auth/passwordreset/${userId}/change`, req, { params: { token: encodeURIComponent(token!) }})
    }

    public async allOrders(): Promise<AxiosResponse<OrdersResponse>> {
      return await this.http.get<OrdersResponse>(`/orders`);
    }

    public async searchOrders(params?: OrderSearchParams): Promise<AxiosResponse<OrdersResponse>> {
      return await this.http.get<OrdersResponse>(`/orders/search`, { params: { ...params}});
    }

    public async searchOrdersUrl(url: string): Promise<AxiosResponse<OrdersResponse>> {
      return await this.http.get<OrdersResponse>(`${url}`);
    }

    // Pre-Buy Phase 6 - dedicated Kit Orders (nuvia_orders) search.
    public async searchNuviaOrders(params?: NuviaOrderSearchParams): Promise<AxiosResponse<NuviaOrdersResponse>> {
      return await this.http.get<NuviaOrdersResponse>(`/nuviaorders/search`, { params: { ...params}});
    }

    public async searchNuviaOrdersUrl(url: string): Promise<AxiosResponse<NuviaOrdersResponse>> {
      return await this.http.get<NuviaOrdersResponse>(`${url}`);
    }

    public async getNuviaOrder(nuviaOrderId: number | string): Promise<AxiosResponse<NuviaOrderItem>> {
      return await this.http.get<NuviaOrderItem>(`/nuviaorders/${nuviaOrderId}`);
    }

    public async getOrder(orderId: number | string): Promise<AxiosResponse<OrderItem>> {
      return await this.http.get<OrderItem>(`/orders/${orderId}`);
    }

    public async getOrderAvailableTests(orderId: number): Promise<AxiosResponse<UserGroupTest[]>> {
      return await this.http.get<UserGroupTest[]>(`/orders/${orderId}/availableTests`);
    }

    public async getOrganizationAvailableTests(orgId: number | string): Promise<AxiosResponse<UserGroupTest[]>> {
      return await this.http.get<UserGroupTest[]>(`/organizations/${orgId}/availableTests`);
    }

    public async getOrderAttachments(orderId: number | string): Promise<AxiosResponse<OrderItemAttachment>> {
      return await this.http.get<OrderItemAttachment>(`/orders/${orderId}/attachments`)
    }
    
    public async getOrderReports(orderId: number | string): Promise<AxiosResponse<OrderItemReports>> {
      return await this.http.get<OrderItemReports>(`/orders/${orderId}/reports`);
    }

    public async updateOrder(orderId: number | string, req: UpdateOrderRequest): Promise<AxiosResponse<UpdateOrderResponse>> {
      return await this.http.post<UpdateOrderRequest, UpdateOrderResponse>(`/orders/update/?id=${orderId}`, req);
    }

    public async createOrder(req: CreateOrderRequest): Promise<AxiosResponse<OrderItem>> {
      return await this.http.post<CreateOrderRequest, OrderItem>(`/orders`, req);
    }

    public async sendTermsConditionsAcceptance(req: TermsConditionsAcceptance): Promise<AxiosResponse<TermsConditionsAcceptance>> {
      return await this.http.post<TermsConditionsAcceptance, TermsConditionsAcceptance>(`/termsconditionsacceptance`, req);
    }

    // Aug 2026 - per-ORG check (not per-user) - requiresAcceptance is true only when the logged-in
    // user's org uses Stripe billing, has a saved payment method, and NO ONE at that org has
    // accepted terms yet. Also carries a masked payment summary (never raw Stripe IDs) for the
    // popup to display. Drives the app-wide hard-block popup - see StripeTermsAcceptanceGate.
    public async getTermsAcceptanceRequired(): Promise<AxiosResponse<TermsAcceptanceStatus>> {
      return await this.http.get<TermsAcceptanceStatus>(`/termsconditionsacceptance/required`);
    }

    public async duplicateOrder(orderId: number | string): Promise<AxiosResponse<DuplicateOrderResponse>> {
      return await this.http.post<DuplicateOrderResponse>(`/orders/duplicate?id=${orderId}`);
    }

    public async getAccountProfile(): Promise<AxiosResponse<UserProfile>> {
      return await this.http.get<AccountProfile>(`/users/profile`);
    }

    public async updateAccountProfile(profile: UserProfile): Promise<AxiosResponse<UserProfile>> {
      return await this.http.post<UserProfile>(`/users/profile`, profile);
    }

    public async allAccountReps(): Promise<AxiosResponse<AccountRepsResponse>> {
      return await this.http.get<AccountRepsResponse>(`/accountreps`);
    }

    public async allUsers(): Promise<AxiosResponse<UsersResponse>> {
      return await this.http.get<UsersResponse>(`/users`);
    }

    public async searchUsers(params?: UsersSearchParams): Promise<AxiosResponse<UsersSearchResponse>> {
      return await this.http.get<UsersSearchResponse>(`/users/search`, { params: { ...params }});
    }

    public async getUser(userId: number | string): Promise<AxiosResponse<UserItem>> {
      return await this.http.get<UserItem>(`/users/${userId}`);
    }

    public async createUser(req: CreateUserRequest): Promise<AxiosResponse<UserItem>> {
      return await this.http.post<CreateUserRequest, UserItem>(`/auth/register`, req);
    }

    public async updateUser(id: string | number, user: UserItem): Promise<AxiosResponse<void>> {
      return await this.http.post<UserItem>(`/users/${id}/update`, user);
    }

    public async getRoles(): Promise<AxiosResponse<UserRole[]>> {
      return await this.http.get<UserRole[]>(`/roles`);
    }

    public async getUserRoles(email?: string): Promise<AxiosResponse<string[]>> {
      return await this.http.get<string[]>(`/roles/getUserRoles`, { params: { email }});
    }

    public async removeUserFromRole(email?: string, roleName?: string): Promise<AxiosResponse<void>> {
      return await this.http.post(`/roles/removeUserFromRole`, {}, { params: { email, roleName }});
    }

    public async addUserToRole(email?: string, roleName?: string): Promise<AxiosResponse<void>> {
      return await this.http.post(`/roles/addUserToRole`, {}, { params: { email, roleName }});
    }

    public async getOrganization(orgId: string | number): Promise<AxiosResponse<OrganizationItem>> {
      return await this.http.get<OrganizationItem>(`/organizations/${orgId}`);
    }

    public async createOrganization(req: CreateOrganizationRequest): Promise<AxiosResponse<OrganizationItem>> {
      return await this.http.post<CreateOrganizationRequest, OrganizationItem>(`/organizations`, req);
    }

    public async getOrganizationOrderStats(orgId: string | number): Promise<AxiosResponse<OrderStats>> {
      return await this.http.get<OrderStats>(`/organizations/${orgId}/orderStats`);
    }

    public async cancelSubscription(id: number | undefined): Promise<AxiosResponse<void>> {
      return await this.http.post<string>(`/organizationbilling/cancelsubscription/${id}`);
    }

    public async getOrganizationBilling(orgId: string | number): Promise<AxiosResponse<OrganizationBilling[]>> {
      return await this.http.get<OrganizationBilling[]>(`/organizationbilling/${orgId}`);
    }

    public async getOrganizationStripeBilling(orgId: string | number): Promise<AxiosResponse<OrganizationStripeBilling>> {
      return await this.http.get<OrganizationStripeBilling>(`/organizationstripebilling/${orgId}`);
    }

    // Aug 2026 - resolves Enterprise vs the org's own billing (see ResolvedPaymentSource) - use
    // this for any "payment method on file" display, not getOrganizationStripeBilling above.
    public async getResolvedPaymentSource(orgId: string | number): Promise<AxiosResponse<ResolvedPaymentSource>> {
      return await this.http.get<ResolvedPaymentSource>(`/organizationstripebilling/${orgId}/resolved`);
    }

    public async updateOrganizationStripeBilling(id: string | number, paymentMethodId : string): Promise<AxiosResponse<string>> {
      return await this.http.post(`/organizationstripebilling/updatepaymentmethod`, {},  { params: { id, paymentMethodId }});
    }

    // Sep 2026 - "Remove Payment Method" - previously the only way to change a payment method on
    // file was to overwrite it with a new one; there was no way to clear it back to none at all.
    public async removeOrganizationStripeBilling(id: string | number): Promise<AxiosResponse<void>> {
      return await this.http.post(`/organizationstripebilling/removepaymentmethod`, {}, { params: { id } });
    }

    public async createOrganizationStripeCustomer(orgId: string | number, name: string | undefined, postalcode: string | undefined): Promise<AxiosResponse<string>> {
      return await this.http.post(`/organizationstripebilling`, {}, { params: { orgId, name, postalcode }});
    }

    public async createStripeSetupIntent(customerId: string | undefined ): Promise<AxiosResponse<string>> {
      return await this.http.post(`/stripeprocessing/createsetupintent`, {}, { params: { customerId }} );
    }

    // Sep 2026 - Phase 3 of the ACH microdeposit-verification fix - called the moment Stripe's
    // confirmSetup returns "requires_action" for microdeposits, and to check on it afterward.
    public async markOrganizationPendingVerification(id: string | number, setupIntentId: string): Promise<AxiosResponse<void>> {
      return await this.http.post(`/organizationstripebilling/${id}/pendingverification`, {}, { params: { setupIntentId } });
    }

    public async checkOrganizationPendingVerification(id: string | number): Promise<AxiosResponse<CheckPendingVerificationResult>> {
      return await this.http.get<CheckPendingVerificationResult>(`/organizationstripebilling/${id}/pendingverification`);
    }

    public async markEnterprisePendingVerification(setupIntentId: string): Promise<AxiosResponse<void>> {
      return await this.http.post(`/enterprisestripebilling/mine/pendingverification`, {}, { params: { setupIntentId } });
    }

    public async checkEnterprisePendingVerification(): Promise<AxiosResponse<CheckPendingVerificationResult>> {
      return await this.http.get<CheckPendingVerificationResult>(`/enterprisestripebilling/mine/pendingverification`);
    }

    public async createStripePaymentIntent(req: StripePaymentIntentRequest ): Promise<AxiosResponse<string>> {
      return await this.http.post<StripePaymentIntentRequest>(`/stripeprocessing/createpaymentintent`, req);
    }

    public async getOrganizationBillingReports(orgId: string | number): Promise<AxiosResponse<OrganizationBillingReport[]>> {
      return await this.http.get<OrganizationBillingReport[]>(`/organizationbillingreport/${orgId}`);
    }
    
    public async getOrganizationBillingReport(reportName: string | undefined ): Promise<AxiosResponse<OrganizationBillingReport>> {
      return await this.http.get<OrganizationBillingReport>(`/organizationbillingreport/report/${reportName}`);
    }

    public async getOrganizationActivityReports(orgId: string | number ): Promise<AxiosResponse<OrganizationActivityReport[]>> {
      return await this.http.get<OrganizationActivityReport[]>(`/organizationbillingreport/activityreports/${orgId}`);
    }

    public async getOrganizationActivityReport(reportId: number | undefined ): Promise<AxiosResponse<OrganizationActivityReport>> {
      return await this.http.get<OrganizationActivityReport>(`/organizationbillingreport/activityreport/${reportId}`);
    }
    
    public async createOrganizationBilling(req: CreateOrganizationBillingRequest): Promise<AxiosResponse<OrganizationBilling>> {
      return await this.http.post<CreateOrganizationBillingRequest, OrganizationBilling>(`/organizationbilling`, req);
    }

    public async updateOrganization(id: string | number, { orgId: _, ...req} : OrganizationItem): Promise<AxiosResponse<void>> {
      return await this.http.post<UpdateOrganizationRequest>(`/organizations/update`, req, { params: { id }});
    }
    
    public async createTransaction(req: CreateTransactionRequest): Promise<AxiosResponse<CreateTransactionResponse>> {
      return await this.http.post<CreateTransactionRequest, CreateTransactionResponse>(`/organizationbilling/createtransaction`, req);
    }

    public async allOrganizations(): Promise<AxiosResponse<OrganizationsResponse>> {
      return await this.http.get<OrganizationsResponse>(`/organizations`);
    }

    public async searchOrganizations(params?: OrganizationSearchParams): Promise<AxiosResponse<OrganizationsSearchResponse>> {
      return await this.http.get<OrganizationsSearchResponse>(`/organizations/search`, { params: { ...params }});
    }

    public async getOrganizationUsers(orgId: number | string, params?: OrganizationGridParams): Promise<AxiosResponse<UsersResponse>> {
      return await this.http.get<UsersResponse>(`/organizations/${orgId}/users`, { params: { ...params }});
    }

    public async getUserGroups(): Promise<AxiosResponse<UserGroupsResponse>> {
      return await this.http.get<UserGroupsResponse>(`/userGroups`);
    }

    public async getUserGroup(id: string | number): Promise<AxiosResponse<UserGroup>> {
      return await this.http.get<UserGroup>(`/userGroups/${id}`);
    }

    public async createUserGroup(req: CreateUserGroupRequest): Promise<AxiosResponse<UserGroup>> {
      return await this.http.post<CreateUserGroupRequest, UserGroup>(`/userGroups`, req);
    }

    public async updateUserGroup(id: string | number, req: UpdateUserGroupRequest): Promise<AxiosResponse<UserGroup>> {
      return await this.http.post<UpdateUserGroupRequest, UserGroup>(`/userGroups/update`, req, { params: { id }});
    }

    public async getUserAvailableTests(): Promise<AxiosResponse<UserGroupTest[]>> {
      return await this.http.get<UserGroupTest[]>(`/users/availableTests`);
    }
    
    public async getUserGroupTestPrices(userGroupId: string | number): Promise<AxiosResponse<UserGroupTestPrice>> {
      return await this.http.get<UserGroupTestPrice>(`/userGroups/${userGroupId}/testprices`);
    }

    // Admin Pricing page
    public async getUserGroupTests(userGroupId: string | number): Promise<AxiosResponse<UserGroupTest[]>> {
      return await this.http.get<UserGroupTest[]>(`/userGroups/${userGroupId}/tests`);
    }

    public async getAllUserGroupTestPrices(userGroupId: string | number): Promise<AxiosResponse<UserGroupTestPrice[]>> {
      return await this.http.get<UserGroupTestPrice[]>(`/userGroups/${userGroupId}/testprices/all`);
    }

    public async createUserGroupTest(userGroupId: string | number, req: CreateUserGroupTestRequest): Promise<AxiosResponse<UserGroupTest>> {
      return await this.http.post<CreateUserGroupTestRequest, UserGroupTest>(`/userGroups/${userGroupId}/tests`, req);
    }

    public async updateUserGroupTest(userGroupId: string | number, testId: number, req: UpdateUserGroupTestRequest): Promise<AxiosResponse<UserGroupTest>> {
      return await this.http.put<UpdateUserGroupTestRequest, UserGroupTest>(`/userGroups/${userGroupId}/tests/${testId}`, req);
    }

    public async upsertUserGroupTestPrice(userGroupId: string | number, testCode: string, req: UpsertUserGroupTestPriceRequest): Promise<AxiosResponse<UserGroupTestPrice>> {
      return await this.http.put<UpsertUserGroupTestPriceRequest, UserGroupTestPrice>(`/userGroups/${userGroupId}/testprices/${encodeURIComponent(testCode)}`, req);
    }

    public async getSpecimenSources(userGroupId: string | number): Promise<AxiosResponse<SpecimenSource[]>> {
      return await this.http.get<SpecimenSource[]>(`/userGroups/${userGroupId}/specimensources`);
    }

    public async createSpecimenSource(userGroupId: string | number, req: CreateSpecimenSourceRequest): Promise<AxiosResponse<SpecimenSource>> {
      return await this.http.post<CreateSpecimenSourceRequest, SpecimenSource>(`/userGroups/${userGroupId}/specimensources`, req);
    }

    public async setTestSpecimenSources(userGroupId: string | number, testId: number, req: SetTestSpecimenSourcesRequest): Promise<AxiosResponse<SpecimenSource[]>> {
      return await this.http.put<SetTestSpecimenSourcesRequest, SpecimenSource[]>(`/userGroups/${userGroupId}/tests/${testId}/specimensources`, req);
    }

    // Platform Maintenance "Test Catalog"
    public async getTestCatalog(): Promise<AxiosResponse<TestCatalogEntry[]>> {
      return await this.http.get<TestCatalogEntry[]>(`/testcatalog`);
    }

    public async createTestCatalogEntry(req: CreateTestCatalogEntryRequest): Promise<AxiosResponse<TestCatalogEntry>> {
      return await this.http.post<CreateTestCatalogEntryRequest, TestCatalogEntry>(`/testcatalog`, req);
    }

    public async updateTestCatalogEntry(id: number, req: UpdateTestCatalogEntryRequest): Promise<AxiosResponse<TestCatalogEntry>> {
      return await this.http.put<UpdateTestCatalogEntryRequest, TestCatalogEntry>(`/testcatalog/${id}`, req);
    }

    // Platform Maintenance "Platform Fees"
    public async getPlatformFees(): Promise<AxiosResponse<PlatformFee[]>> {
      return await this.http.get<PlatformFee[]>(`/platformfees`);
    }

    public async updatePlatformFee(id: number, req: UpdatePlatformFeeRequest): Promise<AxiosResponse<PlatformFee>> {
      return await this.http.put<UpdatePlatformFeeRequest, PlatformFee>(`/platformfees/${id}`, req);
    }

    // Oct 2026 - Platform Maintenance "Platform Settings"
    public async getPlatformSettings(): Promise<AxiosResponse<PlatformSetting[]>> {
      return await this.http.get<PlatformSetting[]>(`/platformsettings`);
    }

    public async updatePlatformSetting(id: number, req: UpdatePlatformSettingRequest): Promise<AxiosResponse<PlatformSetting>> {
      return await this.http.put<UpdatePlatformSettingRequest, PlatformSetting>(`/platformsettings/${id}`, req);
    }

    // Enterprise/billing feature
    public async getEnterprises(): Promise<AxiosResponse<Enterprise[]>> {
      return await this.http.get<Enterprise[]>(`/enterprises`);
    }

    public async getEnterprise(id: string | number): Promise<AxiosResponse<Enterprise>> {
      return await this.http.get<Enterprise>(`/enterprises/${id}`);
    }

    public async createEnterprise(req: CreateEnterpriseRequest): Promise<AxiosResponse<Enterprise>> {
      return await this.http.post<CreateEnterpriseRequest, Enterprise>(`/enterprises`, req);
    }

    public async updateEnterprise(id: string | number, req: UpdateEnterpriseRequest): Promise<AxiosResponse<Enterprise>> {
      return await this.http.put<UpdateEnterpriseRequest, Enterprise>(`/enterprises/${id}`, req);
    }

    // Platform Maintenance "System Banner" tool
    public async getSystemBanners(): Promise<AxiosResponse<SystemBannerItem[]>> {
      return await this.http.get<SystemBannerItem[]>(`/systembanners`);
    }

    // Aug 2026 - hit by every logged-in user, every page load (see AuthenticatedLayout) - not
    // Portal-Admin-gated server-side, unlike every other endpoint on this controller.
    public async getActiveSystemBanner(): Promise<AxiosResponse<SystemBannerItem>> {
      return await this.http.get<SystemBannerItem>(`/systembanners/active`);
    }

    public async createSystemBanner(req: CreateSystemBannerRequest): Promise<AxiosResponse<SystemBannerItem>> {
      return await this.http.post<CreateSystemBannerRequest, SystemBannerItem>(`/systembanners`, req);
    }

    public async updateSystemBanner(id: string | number, req: UpdateSystemBannerRequest): Promise<AxiosResponse<SystemBannerItem>> {
      return await this.http.put<UpdateSystemBannerRequest, SystemBannerItem>(`/systembanners/${id}`, req);
    }

    public async deleteSystemBanner(id: string | number): Promise<AxiosResponse<void>> {
      return await this.http.delete<void>(`/systembanners/${id}`);
    }

    // Aug 2026 - any authenticated user (not Portal-Admin-gated) - records that they closed the
    // currently-active banner for the rest of their current login session. No-op server-side for a
    // banner that isn't AllowClose, so it's always safe to call whenever a close button was shown.
    public async closeSystemBanner(id: string | number): Promise<AxiosResponse<void>> {
      return await this.http.post<void, void>(`/systembanners/${id}/close`);
    }

    public async getSystemBannerStats(id: string | number): Promise<AxiosResponse<SystemBannerStats>> {
      return await this.http.get<SystemBannerStats>(`/systembanners/${id}/stats`);
    }

    // Aug 2026 - Portal Admin manual "start fresh" action - clears every user's shown/closed state
    // for this banner, so a same-session close no longer suppresses it and a just-raised
    // MaxTimesShown takes effect immediately instead of waiting for everyone's next sign-in.
    public async resetSystemBannerViews(id: string | number): Promise<AxiosResponse<{ usersReset: number }>> {
      return await this.http.post<void, { usersReset: number }>(`/systembanners/${id}/reset-views`);
    }

    // Self-service only - the EnterpriseId is always resolved server-side from the caller's own
    // org, never accepted from the client. See EnterpriseStripeBillingController.
    public async getMyEnterpriseStripeBilling(): Promise<AxiosResponse<Enterprise>> {
      return await this.http.get<Enterprise>(`/enterprisestripebilling/mine`);
    }

    public async createMyEnterpriseStripeCustomer(name: string | undefined, postalcode: string | undefined): Promise<AxiosResponse<string>> {
      return await this.http.post(`/enterprisestripebilling/mine`, {}, { params: { name, postalcode } });
    }

    public async updateMyEnterpriseStripeBilling(paymentMethodId: string): Promise<AxiosResponse<string>> {
      return await this.http.post(`/enterprisestripebilling/mine/updatepaymentmethod`, {}, { params: { paymentMethodId } });
    }

    public async removeMyEnterpriseStripeBilling(): Promise<AxiosResponse<void>> {
      return await this.http.post(`/enterprisestripebilling/mine/removepaymentmethod`, {});
    }

    // Health check feature - Portal Admin "Check Now", JWT-authenticated. The API-key-gated
    // GET /health (for external monitors) isn't called from the client at all.
    public async getHealthCheck(): Promise<AxiosResponse<HealthCheckResponse>> {
      return await this.http.get<HealthCheckResponse>(`/health/manual`);
    }

    // Sep 2026 - which commit this deployment is actually running - see HealthController.
    // GetVersion's own comment. Anonymous endpoint (no auth header needed) - most useful exactly
    // when something else is broken and you need to confirm what's really deployed.
    public async getHealthVersion(): Promise<AxiosResponse<HealthVersion>> {
      return await this.http.get<HealthVersion>(`/health/version`);
    }

    // Pre-Buy Phase 1 - Kit Inventory
    public async searchKits(params?: KitSearchParams): Promise<AxiosResponse<KitItem[]>> {
      return await this.http.get<KitItem[]>(`/kits`, { params: { ...params }});
    }

    public async getKit(kitId: string): Promise<AxiosResponse<KitItem>> {
      return await this.http.get<KitItem>(`/kits/${kitId}`);
    }

    // Pre-Buy Phase 2 - Flow 1 (Ship to Clinic)
    public async createKitOrder(req: CreateKitOrderRequest): Promise<AxiosResponse<KitOrderConfirmation>> {
      return await this.http.post<CreateKitOrderRequest, KitOrderConfirmation>(`/kitorders`, req);
    }

    public async getSkus(): Promise<AxiosResponse<SkuCode[]>> {
      return await this.http.get<SkuCode[]>(`/skus`);
    }

    public async syncSkus(): Promise<AxiosResponse<SkuSyncResult>> {
      return await this.http.post(`/skus/sync`, {});
    }

    // Aug 2026 - Tracking Maintenance screen - purely a function of the caller's own filter
    // (date range + which problem categories to include) rather than always fetching every order
    // needing attention and filtering client-side.
    public async getPendingOrderTracking(filter: OrderTrackingMaintenanceFilter): Promise<AxiosResponse<OrderTrackingMaintenanceItem[]>> {
      return await this.http.get<OrderTrackingMaintenanceItem[]>(`/ordertracking/pending`, { params: filter });
    }

    public async updateOrderTracking(orderId: number, returnTrack: string | null): Promise<AxiosResponse<UpdateOrderTrackingResult>> {
      return await this.http.put(`/ordertracking/${orderId}`, { returnTrack });
    }

    // Pre-Buy Phase 3 - Flow 2 (Ship to Patient)
    public async createShipToPatientOrder(req: CreateShipToPatientOrderRequest): Promise<AxiosResponse<ShipToPatientOrderConfirmation>> {
      return await this.http.post<CreateShipToPatientOrderRequest, ShipToPatientOrderConfirmation>(`/shiptopatient`, req);
    }

    // Public/unauthenticated - reached via the emailed payment-link token, no idToken involved.
    // session is this browser's own verify-code session token (see sessionStorage in the
    // patientPayment sagas) - omitted entirely on a fresh/incognito window, which is what makes
    // the server re-require code verification there.
    public async getPaymentLink(token: string, session?: string): Promise<AxiosResponse<PaymentLinkInfo>> {
      return await this.http.get<PaymentLinkInfo>(`/paymentlinks/${token}`, { params: { session } });
    }

    public async completePaymentLink(token: string, req: CompletePaymentLinkRequest): Promise<AxiosResponse<CompletePaymentLinkConfirmation>> {
      return await this.http.post<CompletePaymentLinkRequest, CompletePaymentLinkConfirmation>(`/paymentlinks/${token}/complete`, req);
    }

    public async sendPaymentLinkVerificationCode(token: string): Promise<AxiosResponse<SendVerificationCodeResponse>> {
      return await this.http.post<{}, SendVerificationCodeResponse>(`/paymentlinks/${token}/send-code`, {});
    }

    public async verifyPaymentLinkCode(token: string, req: VerifyCodeRequest): Promise<AxiosResponse<PaymentLinkInfo>> {
      return await this.http.post<VerifyCodeRequest, PaymentLinkInfo>(`/paymentlinks/${token}/verify-code`, req);
    }

    // Pre-Buy Phase 4 - Flow 3 (Clinic Submission)
    public async getAvailableKits(orgId?: number): Promise<AxiosResponse<KitItem[]>> {
      return await this.http.get<KitItem[]>(`/kits/available`, { params: { orgId } });
    }

    // Oct 2026 - Clinic Submission: is a typed-in (Consignment Kit) id actually one of this org's pre-bought kits? Same orgId pattern as
    // getAvailableKits - only needed for a Portal Admin viewing an org other than their own.
    public async getKitPrePaid(kitId: string, orgId?: number): Promise<AxiosResponse<KitPrePaid>> {
      return await this.http.get<KitPrePaid>(`/kits/prepaid/${encodeURIComponent(kitId)}`, { params: { orgId } });
    }

    public async chargeSubmissionFee(req: ChargeSubmissionFeeRequest): Promise<AxiosResponse<ChargeSubmissionFeeConfirmation>> {
      return await this.http.post<ChargeSubmissionFeeRequest, ChargeSubmissionFeeConfirmation>(`/clinicsubmission/submission-fee`, req);
    }

    public async reverseSubmissionFeeCharge(req: ReverseChargeRequest): Promise<AxiosResponse<ReverseChargeResult>> {
      return await this.http.post<ReverseChargeRequest, ReverseChargeResult>(`/clinicsubmission/reverse-charge`, req);
    }

    // Pre-Buy kit tracking (UPS) - on-demand check, no recurring poller yet.
    public async checkOrderTracking(orderId: number): Promise<AxiosResponse<CheckTrackingResult>> {
      return await this.http.post<{}, CheckTrackingResult>(`/kits/checkTracking/${orderId}`, {});
    }

    // Platform Maintenance "Check UPS Tracking" admin tool - ad-hoc lookup, no order/kit tie-in.
    public async checkUpsTrackingNumber(trackingNumber: string): Promise<AxiosResponse<AdHocTrackingResult>> {
      return await this.http.get<AdHocTrackingResult>(`/ups/track/${encodeURIComponent(trackingNumber)}`);
    }

    // Oct 2026 - Platform Maintenance "Kit Origin Lookup" (Portal Admin only). Each section of a lookup is its own call so the
    // drawer can fill them in as they answer; lookupId groups one search's calls in the audit log.
    public async kitLookupLocal(q: string, codes: string[] | undefined, lookupId: string): Promise<AxiosResponse<KitLookupLocal>> {
      return await this.http.get<KitLookupLocal>(`/kitlookup/local`, { params: { q, codes: codes?.length ? codes.join(',') : undefined, lookupId } });
    }

    public async kitLookupNuvia(q: string, lookupId: string): Promise<AxiosResponse<KitLookupNuvia>> {
      return await this.http.get<KitLookupNuvia>(`/kitlookup/nuvia`, { params: { q, lookupId } });
    }

    public async kitLookupUps(trackingNumber: string, lookupId: string): Promise<AxiosResponse<KitLookupUps>> {
      return await this.http.get<KitLookupUps>(`/kitlookup/ups`, { params: { trackingNumber, lookupId } });
    }

    public async kitLookupSubLot(identifier: string, page: number, pageSize: number, lookupId: string): Promise<AxiosResponse<KitLookupSubLot>> {
      return await this.http.get<KitLookupSubLot>(`/kitlookup/sublot`, { params: { identifier, page, pageSize, lookupId } });
    }

    public async getKitLookupFlags(status: 'Open' | 'Reviewed' | 'All'): Promise<AxiosResponse<KitLookupFlag[]>> {
      return await this.http.get<KitLookupFlag[]>(`/kitlookup/flags`, { params: { status } });
    }

    public async createKitLookupFlag(req: CreateKitLookupFlagRequest): Promise<AxiosResponse<KitLookupFlag>> {
      return await this.http.post<CreateKitLookupFlagRequest, KitLookupFlag>(`/kitlookup/flags`, req);
    }

    public async reviewKitLookupFlag(id: number, req: ReviewKitLookupFlagRequest): Promise<AxiosResponse<KitLookupFlag>> {
      return await this.http.put<ReviewKitLookupFlagRequest, KitLookupFlag>(`/kitlookup/flags/${id}/review`, req);
    }

    // Testing Inventory's "track" icon - looks up the order-level (outer package) tracking
    // number(s) rather than any individual kit's own send tracking number. Aug 2026 - returns both
    // legs (Outbound/Return) for a Ship to Patient order, not just the outbound one - see
    // OrderTrackingResult. Same rich per-leg shape as the ad-hoc admin lookup above, but scoped to
    // the caller's own org - any authenticated user can call this, not just Portal Admin.
    // Sep 2026 - orgId is only needed for a Portal Admin (or other true multi-org admin) viewing
    // an org that isn't their own home org via the org switcher - omit for a normal org-scoped
    // caller, same pattern getAvailableKits already uses.
    public async checkKitOrderTracking(nuviaOrderId: number, orgId?: number): Promise<AxiosResponse<OrderTrackingResult>> {
      const query = orgId != null ? `?orgId=${orgId}` : '';
      return await this.http.post<{}, OrderTrackingResult>(`/kits/checkOrderTracking/${nuviaOrderId}${query}`, {});
    }

    // Platform Maintenance "Shipment Tracking Scheduler" admin tool - "Run Now" trigger and run
    // history. The scheduler's own automated calls go through a separate API-key-gated endpoint
    // (not exposed here) that Windows Task Scheduler hits directly, not the SPA.
    //
    // Sep 2026 - this used to return the full CheckAllTrackingResult once the whole check finished
    // server-side, which at real order volume took long enough to hit a reverse proxy timeout (502
    // Proxy Error in the browser). The server now runs the check in the background and returns
    // TrackingRunStarted almost immediately - the saga polls getTrackingSchedulerHistory instead.
    public async runTrackingSchedulerManually(): Promise<AxiosResponse<TrackingRunStarted>> {
      return await this.http.post<{}, TrackingRunStarted>(`/kits/checkAllTracking/manual`, {});
    }

    public async getTrackingSchedulerHistory(limit?: number): Promise<AxiosResponse<SchedulerRunHistoryItem[]>> {
      return await this.http.get<SchedulerRunHistoryItem[]>(`/kits/checkAllTracking/history`, { params: { limit } });
    }

    // Sep 2026 - separate from history above: this reflects a run CURRENTLY in progress (see
    // TrackingSchedulerRunCoordinator), which has no history row until it finishes.
    public async getTrackingSchedulerStatus(): Promise<AxiosResponse<SchedulerRunStatus>> {
      return await this.http.get<SchedulerRunStatus>(`/kits/checkAllTracking/status`);
    }

    // Sep 2026 - Phase D of the orphaned-charge fix - Platform Maintenance "Orphaned Charge
    // Reconciliation" tool. Same background-run/poll-for-completion shape as the tracking
    // scheduler above.
    public async runOrphanedChargesCheckManually(): Promise<AxiosResponse<TrackingRunStarted>> {
      return await this.http.post<{}, TrackingRunStarted>(`/clinicsubmission/checkOrphanedCharges/manual`, {});
    }

    public async getOrphanedChargesHistory(limit?: number): Promise<AxiosResponse<ChargeReconciliationRunHistoryItem[]>> {
      return await this.http.get<ChargeReconciliationRunHistoryItem[]>(`/clinicsubmission/checkOrphanedCharges/history`, { params: { limit } });
    }

    public async getOrphanedChargesStatus(): Promise<AxiosResponse<SchedulerRunStatus>> {
      return await this.http.get<SchedulerRunStatus>(`/clinicsubmission/checkOrphanedCharges/status`);
    }

    // Sep 2026 - the live "Needs Review" worklist - the current set of unresolved orphaned
    // charges, computed fresh on every call. A Portal Admin reviews each row and, if it's really
    // money with nothing to show for it, reverses it via reverseSubmissionFeeCharge with a typed
    // reason.
    public async getOrphanedChargesOutstanding(): Promise<AxiosResponse<OrphanedCharge[]>> {
      return await this.http.get<OrphanedCharge[]>(`/clinicsubmission/checkOrphanedCharges/outstanding`);
    }

    // Sep 2026 - "History" tab - charges that have already been reversed, newest first, with who
    // reversed it and why. The other half of the worklist, once a row leaves the outstanding list.
    public async getOrphanedChargesResolved(limit?: number): Promise<AxiosResponse<OrphanedCharge[]>> {
      return await this.http.get<OrphanedCharge[]>(`/clinicsubmission/checkOrphanedCharges/resolved`, { params: { limit } });
    }

    // Platform Maintenance "Backfill Card Funding" admin tool - same background-run/poll shape as
    // the tracking scheduler above, and for the same reason (a live Stripe lookup per org can
    // outlast a reverse proxy timeout if run inside the request).
    public async backfillCardFunding(): Promise<AxiosResponse<CardFundingBackfillStarted>> {
      return await this.http.post<{}, CardFundingBackfillStarted>(`/organizationstripebilling/backfillcardfunding`, {});
    }

    public async getCardFundingBackfillStatus(): Promise<AxiosResponse<CardFundingBackfillStatus>> {
      return await this.http.get<CardFundingBackfillStatus>(`/organizationstripebilling/backfillcardfunding/status`);
    }

    public async getCardFundingBackfillHistory(limit?: number): Promise<AxiosResponse<CardFundingBackfillHistoryItem[]>> {
      return await this.http.get<CardFundingBackfillHistoryItem[]>(`/organizationstripebilling/backfillcardfunding/history`, { params: { limit } });
    }

    // Platform Maintenance "Analytics" dashboard (Portal Admin only) - from/to as ISO date strings.
    public async getAnalyticsDashboard(from: string, to: string): Promise<AxiosResponse<AnalyticsDashboard>> {
      return await this.http.get<AnalyticsDashboard>(`/analytics/dashboard`, { params: { from, to } });
    }

    // Sep 2026 - backs the "Stripe Setup Adoption" tile's drill-down, fetched only when that
    // drill-down is actually opened rather than as part of every getAnalyticsDashboard call. No
    // date params - this is a current-state snapshot, not date-scoped.
    public async getStripeSetupOrgs(): Promise<AxiosResponse<StripeSetupOrgs>> {
      return await this.http.get<StripeSetupOrgs>(`/analytics/stripe-setups`);
    }

    // Sep 2026 - Revenue & Lab dashboard's Daily/Monthly/YTD tabs. `params` picks the specific
    // date/month+year/year (omit for today/this month/this year) - the server resolves the
    // comparison window from that alone (on-the-fly against real data for Daily, deterministically
    // for Monthly/YTD), so the tab's own picker selection and the numbers behind it can never drift
    // apart (see AnalyticsController.GetRevenueLab).
    public async getRevenueLabAnalytics(period: RevenueLabPeriod, params?: RevenueLabPeriodParams): Promise<AxiosResponse<RevenueLabAnalytics>> {
      return await this.http.get<RevenueLabAnalytics>(`/analytics/revenue-lab`, { params: { period, ...params } });
    }

    // Sep 2026 - Revenue Analytics tiles' drill-downs - `bucket` picks which of the same filters
    // BuildRevenueSectionAsync's own totals use, so a drill-down's rows always sum to exactly the
    // tile's own number. `params` must match whatever date/month+year/year is currently selected,
    // same reasoning as getRevenueLabAnalytics above.
    public async getRevenueDetail(period: RevenueLabPeriod, bucket: RevenueDetailBucket, params?: RevenueLabPeriodParams): Promise<AxiosResponse<RevenueDetailRow[]>> {
      return await this.http.get<RevenueDetailRow[]>(`/analytics/revenue-detail`, { params: { period, bucket, ...params } });
    }

    public async getLabDetail(period: RevenueLabPeriod, bucket: LabDetailBucket, params?: RevenueLabPeriodParams): Promise<AxiosResponse<LabDetailRow[]>> {
      return await this.http.get<LabDetailRow[]>(`/analytics/lab-detail`, { params: { period, bucket, ...params } });
    }

    // Sep 2026 - backs the "Total Orders" tile's drill-down, fetched only when that drill-down is
    // actually opened - mirrors getStripeSetupOrgs's on-demand pattern. Same from/to range as
    // getAnalyticsDashboard, passed through separately since the two are fetched independently.
    public async getOrdersDetail(from: string, to: string): Promise<AxiosResponse<OrdersDetail>> {
      return await this.http.get<OrdersDetail>(`/analytics/orders-detail`, { params: { from, to } });
    }

    // Sep 2026 - Platform Activity & Health tiles' drill-downs - same on-demand pattern as
    // getOrdersDetail above.
    public async getActiveUsersDetail(from: string, to: string): Promise<AxiosResponse<ActiveUserDetailRow[]>> {
      return await this.http.get<ActiveUserDetailRow[]>(`/analytics/active-users-detail`, { params: { from, to } });
    }

    public async getActiveOrgsDetail(from: string, to: string): Promise<AxiosResponse<ActiveOrgDetailRow[]>> {
      return await this.http.get<ActiveOrgDetailRow[]>(`/analytics/active-orgs-detail`, { params: { from, to } });
    }

    public async getLoginUsersDetail(from: string, to: string): Promise<AxiosResponse<LoginUserDetailRow[]>> {
      return await this.http.get<LoginUserDetailRow[]>(`/analytics/login-users-detail`, { params: { from, to } });
    }

    public async getErrorDetail(from: string, to: string): Promise<AxiosResponse<ErrorDetailRow[]>> {
      return await this.http.get<ErrorDetailRow[]>(`/analytics/error-detail`, { params: { from, to } });
    }

    public async getBannerViewsDetail(from: string, to: string): Promise<AxiosResponse<BannerViewsDetailRow[]>> {
      return await this.http.get<BannerViewsDetailRow[]>(`/analytics/banner-views-detail`, { params: { from, to } });
    }

    // No date params - current-state snapshot, same as getStripeSetupOrgs.
    public async getSuspendedOrgsDetail(): Promise<AxiosResponse<SuspendedOrgDetailRow[]>> {
      return await this.http.get<SuspendedOrgDetailRow[]>(`/analytics/suspended-orgs-detail`);
    }

    // Sep 2026 - remaining Orders/Pre-Buy/Billing/UPS/Patients tiles' drill-downs - same on-demand
    // pattern as everything above.
    public async getNeedsContactDetail(): Promise<AxiosResponse<NeedsContactDetailRow[]>> {
      return await this.http.get<NeedsContactDetailRow[]>(`/analytics/needs-contact-detail`);
    }

    public async getUnparseableDatesDetail(): Promise<AxiosResponse<UnparseableDateDetailRow[]>> {
      return await this.http.get<UnparseableDateDetailRow[]>(`/analytics/unparseable-dates-detail`);
    }

    public async getTurnaroundDetail(from: string, to: string): Promise<AxiosResponse<TurnaroundDetailRow[]>> {
      return await this.http.get<TurnaroundDetailRow[]>(`/analytics/turnaround-detail`, { params: { from, to } });
    }

    public async getShipToClinicDetail(from: string, to: string): Promise<AxiosResponse<NuviaOrderDetailRow[]>> {
      return await this.http.get<NuviaOrderDetailRow[]>(`/analytics/ship-to-clinic-detail`, { params: { from, to } });
    }

    public async getShipToPatientDetail(from: string, to: string): Promise<AxiosResponse<NuviaOrderDetailRow[]>> {
      return await this.http.get<NuviaOrderDetailRow[]>(`/analytics/ship-to-patient-detail`, { params: { from, to } });
    }

    public async getPreBuyOrgsDetail(): Promise<AxiosResponse<PreBuyOrgDetailRow[]>> {
      return await this.http.get<PreBuyOrgDetailRow[]>(`/analytics/prebuy-orgs-detail`);
    }

    public async getSchedulerRunsDetail(from: string, to: string): Promise<AxiosResponse<SchedulerRunDetailRow[]>> {
      return await this.http.get<SchedulerRunDetailRow[]>(`/analytics/scheduler-runs-detail`, { params: { from, to } });
    }

    public async getShippingRequestsDetail(from: string, to: string): Promise<AxiosResponse<ShippingRequestDetailRow[]>> {
      return await this.http.get<ShippingRequestDetailRow[]>(`/analytics/shipping-requests-detail`, { params: { from, to } });
    }

    public async getPendingPaymentsDetail(from: string, to: string): Promise<AxiosResponse<PendingPaymentDetailRow[]>> {
      return await this.http.get<PendingPaymentDetailRow[]>(`/analytics/pending-payments-detail`, { params: { from, to } });
    }

    public async getStripeChargesDetail(from: string, to: string): Promise<AxiosResponse<BillingHistoryDetailRow[]>> {
      return await this.http.get<BillingHistoryDetailRow[]>(`/analytics/stripe-charges-detail`, { params: { from, to } });
    }

    public async getEProcessingChargesDetail(from: string, to: string): Promise<AxiosResponse<BillingHistoryDetailRow[]>> {
      return await this.http.get<BillingHistoryDetailRow[]>(`/analytics/eprocessing-charges-detail`, { params: { from, to } });
    }

    // Sep 2026 - "Refunded Charges" tile's drill-down. Scoped by when the reversal happened, not
    // the original charge date - see BillingAnalytics.refundedChargeAmount's own comment.
    public async getRefundedChargesDetail(from: string, to: string): Promise<AxiosResponse<RefundedChargeDetailRow[]>> {
      return await this.http.get<RefundedChargeDetailRow[]>(`/analytics/refunded-charges-detail`, { params: { from, to } });
    }

    public async getTestCreditEventsDetail(from: string, to: string): Promise<AxiosResponse<BillingHistoryDetailRow[]>> {
      return await this.http.get<BillingHistoryDetailRow[]>(`/analytics/test-credit-events-detail`, { params: { from, to } });
    }

    public async getChargeAuditsDetail(from: string, to: string): Promise<AxiosResponse<ChargeAuditDetailRow[]>> {
      return await this.http.get<ChargeAuditDetailRow[]>(`/analytics/charge-audits-detail`, { params: { from, to } });
    }

    public async getBillingSourceOrgsDetail(): Promise<AxiosResponse<BillingSourceOrgDetailRow[]>> {
      return await this.http.get<BillingSourceOrgDetailRow[]>(`/analytics/billing-source-orgs-detail`);
    }

    public async getDelayedShipmentsDetail(from: string, to: string): Promise<AxiosResponse<DelayedShipmentDetailRow[]>> {
      return await this.http.get<DelayedShipmentDetailRow[]>(`/analytics/delayed-shipments-detail`, { params: { from, to } });
    }

    public async getTransitTimeDetail(from: string, to: string): Promise<AxiosResponse<TransitTimeDetailRow[]>> {
      return await this.http.get<TransitTimeDetailRow[]>(`/analytics/transit-time-detail`, { params: { from, to } });
    }

    public async getUnknownChecksDetail(from: string, to: string): Promise<AxiosResponse<UnknownCheckDetailRow[]>> {
      return await this.http.get<UnknownCheckDetailRow[]>(`/analytics/unknown-checks-detail`, { params: { from, to } });
    }

    public async getReshipDetail(from: string, to: string): Promise<AxiosResponse<ReshipDetailRow[]>> {
      return await this.http.get<ReshipDetailRow[]>(`/analytics/reship-detail`, { params: { from, to } });
    }

    // Sep 2026 - live, not date-scoped - no from/to params, matching the server endpoint.
    public async getOrdersAwaitingNextCheckDetail(): Promise<AxiosResponse<OrdersAwaitingNextCheckDetailRow[]>> {
      return await this.http.get<OrdersAwaitingNextCheckDetailRow[]>(`/analytics/orders-awaiting-next-check-detail`);
    }

    public async getReturnTrackingCaptureDetail(from: string, to: string): Promise<AxiosResponse<ReturnTrackingCaptureDetailRow[]>> {
      return await this.http.get<ReturnTrackingCaptureDetailRow[]>(`/analytics/return-tracking-capture-detail`, { params: { from, to } });
    }

    public async getPatientsDetail(from: string, to: string): Promise<AxiosResponse<PatientDetailRow[]>> {
      return await this.http.get<PatientDetailRow[]>(`/analytics/patients-detail`, { params: { from, to } });
    }

    // Sep 2026 - Clients section tiles' drill-downs.
    public async getNewClientsDetail(from: string, to: string): Promise<AxiosResponse<NewClientDetailRow[]>> {
      return await this.http.get<NewClientDetailRow[]>(`/analytics/new-clients-detail`, { params: { from, to } });
    }

    public async getNewClientCashDetail(from: string, to: string): Promise<AxiosResponse<BillingHistoryDetailRow[]>> {
      return await this.http.get<BillingHistoryDetailRow[]>(`/analytics/new-client-cash-detail`, { params: { from, to } });
    }

    public async getExistingClientCashDetail(from: string, to: string): Promise<AxiosResponse<BillingHistoryDetailRow[]>> {
      return await this.http.get<BillingHistoryDetailRow[]>(`/analytics/existing-client-cash-detail`, { params: { from, to } });
    }

    public async getClientSamplesDetail(from: string, to: string): Promise<AxiosResponse<ClientSampleDetailRow[]>> {
      return await this.http.get<ClientSampleDetailRow[]>(`/analytics/client-samples-detail`, { params: { from, to } });
    }

    public async getClientResultedDetail(from: string, to: string): Promise<AxiosResponse<ClientResultedDetailRow[]>> {
      return await this.http.get<ClientResultedDetailRow[]>(`/analytics/client-resulted-detail`, { params: { from, to } });
    }

    // Sep 2026 - Platform Maintenance "Notifications" tool (Portal Admin only) - date-filtered
    // list of NotificationDelivery rows, and a per-row manual resend for Exhausted (or any other
    // status - the server doesn't restrict it, only the client only surfaces the button for
    // Exhausted rows). See NotificationsController.
    public async getNotificationDeliveries(filter: NotificationDeliveryFilter): Promise<AxiosResponse<NotificationDelivery[]>> {
      return await this.http.get<NotificationDelivery[]>(`/notifications`, { params: filter });
    }

    public async resendNotificationDelivery(notificationDeliveryId: number): Promise<AxiosResponse<void>> {
      return await this.http.post<{}, void>(`/notifications/${notificationDeliveryId}/resend`, {});
    }

    // Sep 2026 - Platform Maintenance "Executive Report" (Portal Admin only). asOf (yyyy-MM-dd)
    // optional - defaults to today server-side, lets a Portal Admin review a past date's snapshot.
    // Sep 2026 - Purchase History. orgId is only honoured for roles whose scope allows it; the
    // server narrows, never widens, so passing it from an Org Admin's session changes nothing.
    public async getPurchaseHistory(from?: string, to?: string, orgId?: number): Promise<AxiosResponse<PurchaseHistoryResponse>> {
      return await this.http.get<PurchaseHistoryResponse>(`/purchasehistory`, { params: { from, to, orgId } });
    }

    public async getPurchaseReceipt(transactionId: string): Promise<AxiosResponse<PurchaseReceipt>> {
      return await this.http.get<PurchaseReceipt>(`/purchasehistory/${encodeURIComponent(transactionId)}/receipt`);
    }

    // Returns the .xlsx itself. Generated server-side from the same query the list runs, so the
    // download always matches what is on screen rather than being rebuilt from rows the browser
    // happens to hold.
    public async exportPurchaseHistory(from?: string, to?: string, orgId?: number): Promise<AxiosResponse<Blob>> {
      return await this.http.get(`/purchasehistory/export`, { params: { from, to, orgId }, responseType: 'blob' });
    }

    // Sep 2026 - "lets not download an empty file... show the no. of records" - a cheap count
    // check the client runs before calling exportOrderPaymentReport below, so it can tell the user
    // "no records found" without generating anything, or "generating N orders..." when there are.
    public async getOrderPaymentReportCount(from: string, to: string, dateField: 'created' | 'resulted' | 'both'): Promise<AxiosResponse<{ count: number }>> {
      return await this.http.get(`/analytics/order-payment-report/count`, { params: { from, to, dateField } });
    }

    // Sep 2026 - "Order Payment Reconciliation" report, Platform Maintenance -> Reporting &
    // Analytics. dateField picks which date column the range filters by: 'created' (order
    // placement, via OrderCreatedDate falling back to Order_datetime) or 'resulted'
    // (Reported_datetime). Returns the .xlsx itself, same blob-download convention as
    // exportPurchaseHistory above. A 204 (no rows - see getOrderPaymentReportCount above) can
    // still happen here on a race between the count check and this call; the caller handles it.
    public async exportOrderPaymentReport(from: string, to: string, dateField: 'created' | 'resulted' | 'both'): Promise<AxiosResponse<Blob>> {
      return await this.http.get(`/analytics/order-payment-report/export`, { params: { from, to, dateField }, responseType: 'blob' });
    }

    // Nov 2026 - Commercial Report. The report is a POST only so owner/manager names travel in a body, not a query string.
    public async getCommercialReport(request: CommercialReportRequest): Promise<AxiosResponse<CommercialReport>> {
      return await this.http.post<CommercialReportRequest, CommercialReport>(`/commercialreport/report`, request);
    }

    public async getCommercialReportFilters(): Promise<AxiosResponse<CommercialReportFilters>> {
      return await this.http.get<CommercialReportFilters>(`/commercialreport/filters`);
    }

    public async getExecutiveReport(asOf?: string): Promise<AxiosResponse<ExecutiveReport>> {
      return await this.http.get<ExecutiveReport>(`/analytics/executive-report`, { params: { asOf } });
    }

    public async getExecutiveReportRevenueDetail(scope: 'today' | 'mtd' | 'ytd', asOf?: string): Promise<AxiosResponse<ExecutiveReportRevenueEventDetail[]>> {
      return await this.http.get<ExecutiveReportRevenueEventDetail[]>(`/analytics/executive-report/revenue-detail`, { params: { scope, asOf } });
    }

    public async getExecutiveReportCashDetail(scope: 'today' | 'mtd' | 'ytd', asOf?: string): Promise<AxiosResponse<ExecutiveReportCashRowDetail[]>> {
      return await this.http.get<ExecutiveReportCashRowDetail[]>(`/analytics/executive-report/cash-detail`, { params: { scope, asOf } });
    }

    public async getExecutiveReportSamplesDetail(scope: 'ordered' | 'resulted', asOf?: string): Promise<AxiosResponse<ExecutiveReportSampleDetail[]>> {
      return await this.http.get<ExecutiveReportSampleDetail[]>(`/analytics/executive-report/samples-detail`, { params: { scope, asOf } });
    }

    public async getExecutiveReportBacklogDetail(asOf?: string): Promise<AxiosResponse<ExecutiveReportBacklogDetail[]>> {
      return await this.http.get<ExecutiveReportBacklogDetail[]>(`/analytics/executive-report/backlog-detail`, { params: { asOf } });
    }

    public async getExecutiveReportKitFloatDetail(asOf?: string): Promise<AxiosResponse<ExecutiveReportKitFloatDetail[]>> {
      return await this.http.get<ExecutiveReportKitFloatDetail[]>(`/analytics/executive-report/kit-float-detail`, { params: { asOf } });
    }

    // Sep 2026 - no `asOf` - Organization.TestCreditDollars is a live running balance with no
    // ledger behind it, so there's no such thing as "the balance as of a past date" (see the
    // server endpoint's own comment).
    public async getExecutiveReportTestCreditDetail(): Promise<AxiosResponse<ExecutiveReportTestCreditDetail[]>> {
      return await this.http.get<ExecutiveReportTestCreditDetail[]>(`/analytics/executive-report/test-credit-detail`);
    }
}
